import React, { useState, useEffect } from "react";
import { Table, Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import "./expenditure-forecast.css";

interface ExpenditureData {
  idexpenditure_forecast?: number;
  project: string;
  year: number;
  month: number;
  expenditure: number;
  created_at?: Date;
  updated_at?: Date;
}

interface ProjectOption {
  job_number: string;
}

interface ExpenditureForecastProps {
  apiKey: string;
}

const ExpenditureForecast: React.FC<ExpenditureForecastProps> = ({
  apiKey,
}) => {
  const [data, setData] = useState<ExpenditureData[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [editingCell, setEditingCell] = useState<{ month: number } | null>(
    null
  );
  const [tempValue, setTempValue] = useState<string>("");
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [loading, setLoading] = useState<boolean>(false);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Generate year options from 2019 to 2040
  const yearOptions = Array.from(
    { length: 2040 - 2019 + 1 },
    (_, i) => 2019 + i
  );

  // Load projects on component mount
  useEffect(() => {
    if (apiKey) {
      loadProjects();
    } else {
      setProjectsLoading(false);
      setProjects([]);
      setSelectedProject("");
    }
  }, [apiKey]);

  // Load forecast data when project or year changes
  useEffect(() => {
    if (selectedProject && selectedYear && apiKey) {
      loadForecastData();
    }
  }, [selectedProject, selectedYear, apiKey]);

  const loadProjects = async () => {
    if (!apiKey) {
      setProjectsLoading(false);
      return;
    }

    try {
      setProjectsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}expenditure-forecast/projects`,
        {
          headers: { Authorization: apiKey },
          validateStatus: () => true,
        }
      );

      if (response.status === 200) {
        setProjects(response.data);
        if (response.data.length > 0) {
          setSelectedProject(response.data[0].job_number);
        }
      } else {
        console.error("Failed to load projects");
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  };
  const loadForecastData = async () => {
    if (!apiKey || !selectedProject || !selectedYear) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}expenditure-forecast/by-project-year`,
        {
          params: { project: selectedProject, year: selectedYear },
          headers: { Authorization: apiKey },
          validateStatus: () => true,
        }
      );

      if (response.status === 200) {
        setData(response.data);
      } else {
        console.error("Failed to load forecast data");
        setData([]);
      }
    } catch (error) {
      console.error("Error loading forecast data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (month: number) => {
    setEditingCell({ month });
    const currentData = data.find((item) => item.month === month);
    setTempValue(currentData ? currentData.expenditure.toString() : "0");
  };

  const handleCellChange = (value: string) => {
    setTempValue(value);
  };

  const handleCellSave = async () => {
    if (editingCell && selectedProject && selectedYear) {
      const numericValue = parseFloat(tempValue) || 0;

      try {
        setSaveStatus("saving");

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}expenditure-forecast`,
          {
            project: selectedProject,
            year: selectedYear,
            month: editingCell.month,
            expenditure: numericValue,
          },
          {
            headers: {
              Authorization: apiKey,
              "Content-Type": "application/json",
            },
            validateStatus: () => true,
          }
        );

        if (response.status === 200 || response.status === 201) {
          // Update local data
          const newData = [...data];
          const existingIndex = newData.findIndex(
            (item) => item.month === editingCell.month
          );

          if (existingIndex >= 0) {
            newData[existingIndex].expenditure = numericValue;
          } else {
            newData.push({
              project: selectedProject,
              year: selectedYear,
              month: editingCell.month,
              expenditure: numericValue,
            });
            newData.sort((a, b) => a.month - b.month);
          }

          setData(newData);
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } else {
          throw new Error("Failed to save data");
        }
      } catch (error) {
        console.error("Error saving data:", error);
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }

      setEditingCell(null);
      setTempValue("");
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setTempValue("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCellSave();
    } else if (e.key === "Escape") {
      handleCellCancel();
    }
  };

  const renderCell = (month: number) => {
    const currentData = data.find((item) => item.month === month);
    const value = currentData ? currentData.expenditure : 0;
    const isEditing = editingCell?.month === month;

    if (isEditing) {
      return (
        <Form.Control
          type="number"
          step="0.001"
          min="0"
          value={tempValue}
          onChange={(e) => handleCellChange(e.target.value)}
          onBlur={handleCellSave}
          onKeyDown={handleKeyPress}
          autoFocus
          className="expenditure-input"
        />
      );
    }

    return (
      <div
        className="expenditure-cell"
        onClick={() => handleCellClick(month)}
        title="Click to edit"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleCellClick(month);
          }
        }}>
        {value.toFixed(3)}
      </div>
    );
  };

  if (!apiKey) {
    return (
      <Card className="expenditure-forecast-container">
        <Card.Body className="text-center">
          <div className="p-4">
            <h5>Authentication Required</h5>
            <p className="text-muted">
              Please authenticate to access expenditure forecast data.
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (projectsLoading) {
    return (
      <Card className="expenditure-forecast-container">
        <Card.Body className="text-center">
          <Spinner animation="border" />
          <div className="mt-2">Loading projects...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="expenditure-forecast-container">
      <Card.Header>
        <h5>Forecasted Monthly Expenditure (HK$ M)</h5>
      </Card.Header>

      <Card.Body>
        <div className="year-controls">
          <div>
            <Form.Label>Project:</Form.Label>
            <Form.Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="project-selector"
              disabled={projects.length === 0}>
              {projects.map((project) => (
                <option key={project.job_number} value={project.job_number}>
                  {project.job_number}
                </option>
              ))}
            </Form.Select>
          </div>

          <div>
            <Form.Label>Year:</Form.Label>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="year-selector">
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="action-buttons">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadForecastData}
              disabled={loading || !selectedProject}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Loading...
                </>
              ) : (
                "Reload"
              )}
            </Button>

            <div className="save-status">
              {saveStatus === "saving" && (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Saving...</span>
                </>
              )}

              {saveStatus === "saved" && (
                <Alert variant="success" className="mb-0 py-1 px-2">
                  Saved!
                </Alert>
              )}

              {saveStatus === "error" && (
                <Alert variant="danger" className="mb-0 py-1 px-2">
                  Error saving
                </Alert>
              )}
            </div>
          </div>
        </div>

        {selectedProject && (
          <div
            className={`expenditure-table-container ${
              loading ? "loading-overlay" : ""
            }`}>
            <Table bordered className="expenditure-table">
              <thead>
                <tr>
                  <th className="year-header" colSpan={2}>
                    {selectedProject} - {selectedYear}
                  </th>
                </tr>
                <tr>
                  <th>Month</th>
                  <th>Expenditure (HK$ M)</th>
                </tr>
              </thead>
              <tbody>
                {monthNames.map((monthName, index) => {
                  const month = index + 1;
                  return (
                    <tr key={month}>
                      <td className="month-cell">
                        {monthName} {selectedYear.toString().slice(-2)}
                      </td>
                      <td className="value-cell">{renderCell(month)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}>
                <Spinner animation="border" />
              </div>
            )}
          </div>
        )}

        <div className="mt-3">
          <small className="text-muted">
            Click on any expenditure cell to edit values. Press Enter to save or
            Escape to cancel. Data is automatically saved when you finish
            editing each cell.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ExpenditureForecast;
